import { ArrowUpRight, Check, Copy, Mail } from 'lucide-react'
import posthog from 'posthog-js'
import { type ComponentProps, useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib/cn'
import { Button } from '@/shared/ui/Button'

type EmailActionButtonProps = {
  className?: string
  copiedLabel: string
  copyLabel: string
  href: string
  label: string
  showArrow?: boolean
  size?: ComponentProps<typeof Button>['size']
  variant?: ComponentProps<typeof Button>['variant']
}

export function EmailActionButton({
  className,
  copiedLabel,
  copyLabel,
  href,
  label,
  showArrow = false,
  size = 'lg',
  variant = 'primary',
}: EmailActionButtonProps) {
  const [copied, setCopied] = useState(false)
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const emailAddress = decodeURIComponent(href.replace(/^mailto:/, '').split('?')[0])
  const copyButtonLabel = copied ? copiedLabel : copyLabel

  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        clearTimeout(resetTimerRef.current)
      }
    }
  }, [])

  async function handleCopyClick() {
    if (!navigator.clipboard?.writeText) {
      return
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(emailAddress)
      } else {
        copyEmailAddressWithFallback(emailAddress)
      }

      setCopied(true)
      posthog.capture('email_address_copied')

      if (resetTimerRef.current !== null) {
        clearTimeout(resetTimerRef.current)
      }

      resetTimerRef.current = setTimeout(() => setCopied(false), 1600)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className={cn('inline-flex items-stretch', className)}>
      <Button asChild className="rounded-r-none border-r-0" size={size} variant={variant}>
        <a href={href}>
          <Mail className="h-4 w-4" />
          {label}
          {showArrow ? <ArrowUpRight className="h-4 w-4" /> : null}
        </a>
      </Button>
      <Button
        aria-label={copyButtonLabel}
        className="shrink-0 rounded-l-none border-l border-l-primary-foreground/18 px-3"
        onClick={handleCopyClick}
        size={size}
        title={copyButtonLabel}
        type="button"
        variant={variant}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

function copyEmailAddressWithFallback(emailAddress: string) {
  if (typeof document === 'undefined') {
    throw new Error('Document is unavailable')
  }

  const textarea = document.createElement('textarea')
  textarea.value = emailAddress
  textarea.setAttribute('readonly', '')
  textarea.style.left = '-9999px'
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  document.body.append(textarea)
  textarea.select()

  const copied = document.execCommand('copy')
  textarea.remove()

  if (!copied) {
    throw new Error('Copy command failed')
  }
}
